import { atom, useAtom } from "jotai"
import { useCallback } from "react"

const drawerDepthAtom = atom<number>(0)

// Module-level variable to track drawer depth for non-React code (like ResizeObserver callbacks)
// This is synchronized with the atom value
let currentDrawerDepth = 0

// Saved CSS variable value when nested drawer opens
let savedDrawerHeight: string | null = null

/**
 * Get the current drawer depth (for use in non-React code like ResizeObserver callbacks)
 */
export function getDrawerDepth(): number {
    return currentDrawerDepth
}

/**
 * Hook to track nested drawer depth.
 * Used to prevent nested drawers from overwriting CSS variables set by parent drawers.
 *
 * When a drawer opens, it increments the depth counter.
 * When it closes, it decrements the counter.
 * Only the first drawer (depth 0 -> 1) should set global CSS variables like --drawer-content-height.
 */
export default function useDrawerDepth() {
    const [drawerDepth, setDrawerDepth] = useAtom(drawerDepthAtom)

    const incrementDepth = useCallback(() => {
        setDrawerDepth((prev) => {
            const newDepth = prev + 1
            currentDrawerDepth = newDepth

            // When going from depth 1 to 2, save the current CSS variable value
            // and prevent further updates while nested drawer is open
            if (newDepth === 2) {
                const currentHeight = document.documentElement.style.getPropertyValue("--drawer-content-height")
                if (currentHeight) {
                    savedDrawerHeight = currentHeight
                }
            }

            return newDepth
        })
    }, [setDrawerDepth])

    const decrementDepth = useCallback(() => {
        setDrawerDepth((prev) => {
            const newDepth = Math.max(0, prev - 1)
            currentDrawerDepth = newDepth

            // When going from depth 2 to 1, restore the saved CSS variable value
            if (prev === 2 && newDepth === 1 && savedDrawerHeight) {
                document.documentElement.style.setProperty("--drawer-content-height", savedDrawerHeight)
                savedDrawerHeight = null
            }

            return newDepth
        })
    }, [setDrawerDepth])

    return {
        drawerDepth,
        incrementDepth,
        decrementDepth,
    }
}
